import { Trash2 } from "lucide-react";
import dayjs, { Dayjs } from "dayjs";
import React, { FC, useCallback } from 'react'
import { useIntl } from "react-intl";
import { UseFormSetValue, UseFormWatch } from "react-hook-form";

import { Activity, Interval } from "../../../../../../model/activity.model";
import { DATE_TIME_FORMAT } from "../../../../../../constant/format";
import { Button } from "../../../../../ui/button";
import { Label } from "../../../../../ui/label";
import { DateTimeRangePicker } from "../../../../../ui/datetime-range-picker";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../../../../../ui/alert-dialog";

interface IIntervals {
  intervals: Interval[]
  setValue: UseFormSetValue<any>
  watch: UseFormWatch<any>
  setIntervals: React.Dispatch<React.SetStateAction<Interval[]>>
  activity: Partial<Activity>
}

const Intervals: FC<IIntervals> = ({ intervals, setValue, watch, setIntervals, activity }) => {
  const { formatMessage } = useIntl();

  const addNewIntervalCallback = useCallback(() => {
    const newInterval = {
      typeId: activity.typeId,
      activityId: activity.id,
      tags: activity.tags,
      start: new Date().toISOString(),
      finish: new Date().toISOString(),
      comment: '',
    } as Interval;

    setIntervals(intervals => {
      const newIntervals = [...intervals, newInterval];
      setValue('intervals', newIntervals);
      return newIntervals;
    });
  }, [activity, setIntervals, setValue]);

  return (
    <>
      <div className="grid grid-cols-[100px_1fr] items-start gap-4">
        <Label className="pt-2">{formatMessage({ id: 'INTERVALS' })}</Label>
        <div className="space-y-2">
          {intervals.map((interval: Interval, index) => {
            const onChangeInterval = (values: [Dayjs | null, Dayjs | null] | null) => {
              const currentIntervals = watch('intervals');
              const newIntervals = currentIntervals.map((intervalElement: Interval, i: number) => {
                if ((intervalElement.id === interval.id || i === index) && values) {
                  return ({ ...intervalElement, start: values[0]?.toISOString(), finish: values[1]?.toISOString() });
                }
                return intervalElement;
              });

              setIntervals(newIntervals);
              setValue('intervals', newIntervals);
            };

            const deleteCallback = () => {
              setIntervals(intervals => {
                const newIntervals = intervals.filter((_, i) => i !== index);
                setValue('intervals', newIntervals);
                return newIntervals;
              });
            };

            return (
              <div key={interval.id || index} className="flex items-center gap-2">
                <DateTimeRangePicker
                  onChange={onChangeInterval}
                  value={[
                    dayjs(interval.start),
                    dayjs(interval.finish)
                  ]}
                  format={DATE_TIME_FORMAT}
                />
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:text-destructive">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>{formatMessage({ id: 'DELETE_INTERVAL' })}</AlertDialogTitle>
                      <AlertDialogDescription>
                        {formatMessage({ id: 'DELETE_INTERVAL_CONFIRM' })}
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>{formatMessage({ id: 'NO' })}</AlertDialogCancel>
                      <AlertDialogAction onClick={deleteCallback}>{formatMessage({ id: 'YES' })}</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            )
          })}
          <Button type="button" variant="outline" onClick={addNewIntervalCallback}>
            {formatMessage({ id: 'ADD_INTERVAL' })}
          </Button>
        </div>
      </div>
    </>
  );
}

export default Intervals;
